import { z } from 'zod';
import { isoDateTimeSchema, localTimeSchema, uuidSchema } from './common.js';
import { presetRulesSchema } from './preset.js';
/**
 * Admin HLS preview-key signed token payload (TASK-044, ADR-008).
 *
 * The token embedded in `/admin/tracks/:id/playback.m3u8`'s rewritten
 * `EXT-X-KEY URI=...?token=<payload>.<hmac>` is the **base64url-encoded JSON
 * of this shape**, dot-separated from a base64url HMAC-SHA256 over the
 * payload bytes. Verification recomputes the HMAC, parses the payload, and
 * checks every field below.
 *
 * `expires_at` is a server clock value (ms since epoch). TTL — 10 minutes
 * per ADR-008 (5 minutes for the FE-issued token, BUG-161 / ADR-024).
 *
 * `admin_user_id` pins the token to the admin who fetched the m3u8, so a
 * leaked URL cannot be replayed by another admin / session.
 *
 * `role` (ADR-024, BUG-161) lets the BE accept `?token=` as an ALTERNATIVE
 * auth path for the Safari/iPad native-HLS preview: the guard reconstructs
 * the acting user `{id, role}` from the verified payload when no Bearer
 * header is present. Optional for backward compatibility with in-flight
 * tokens minted before the field existed — those tokens still work on the
 * Bearer path but are rejected on the token-only path.
 *
 * `rendition_id` (ADR-046, STORY-075) — the rendition «Прослушать» plays,
 * picked by the rule venues get when the token is minted; the child playlist
 * and the key serve it. Optional: absent means the version's own encode, as in
 * every token minted before renditions existed.
 */
export const previewTokenRoleSchema = z.enum(['admin', 'super_admin', 'selector']);
export const previewKeyTokenPayloadSchema = z.object({
    track_id: uuidSchema,
    version_id: uuidSchema,
    expires_at: z.number().int().positive(),
    admin_user_id: uuidSchema,
    role: previewTokenRoleSchema.optional(),
    rendition_id: uuidSchema.optional(),
});
/**
 * `POST /admin/tracks/:id/preview-token` response (BUG-161, ADR-024).
 *
 * Mints a short-lived (5 min) HMAC token for the track's CURRENT ready
 * version so the Safari/iPad native HLS player — which cannot attach an
 * `Authorization` header — can load `playback.m3u8?token=…`. `expires_at`
 * is ms since epoch, mirroring the token payload's own clock format.
 */
export const trackPreviewTokenResponseSchema = z
    .object({
    token: z.string().min(1),
    expires_at: z.number().int().positive(),
})
    .strict();
// ---------------------------------------------------------------------------
// Player login + heartbeat (Wave 2.14 BE-2, TASK-079).
//
// `POST /player/login` authenticates a venue device with a login / password
// pair admin-provisioned per player row. Single-active-session is enforced
// server-side under ADR-007: a successful login revokes any prior active
// session for the same `player_id`. The device's next heartbeat for the old
// session then returns 401 with `code = 'session_revoked'`.
// ---------------------------------------------------------------------------
/**
 * Playback states reported by the device on each heartbeat. The fourth
 * possible state — `offline` — is NOT in this union because it is derived
 * server-side from `last_heartbeat_at`. Persisting `offline` would race the
 * heartbeat timer.
 */
export const playerStatusSchema = z.enum(['playing', 'paused', 'idle']);
/**
 * What the venue wants, reported on each heartbeat (ios-app-arch §7.5).
 * `paused` only after a person's pause (or an output loss that waits for a
 * tap); `playing` in every other state — loading, an error being retried, a
 * stall, an interruption, a pause the system imposed. `status` keeps saying
 * what the device does now; the offline detector reads this instead.
 */
export const playerPlaybackIntentSchema = z.enum(['playing', 'paused']);
/**
 * `POST /player/login` request body.
 *
 * `venue_id` is mandatory (Wave 2.14 BE-2 R2, C-4): the player credential
 * pair is unique only within a venue (`players_venue_login_unique`), so two
 * different tenants may legitimately provision identical login strings under
 * their own venues. The installer / provisioning bundle ships the venue id
 * into the player config alongside the credential pair.
 *
 * @deprecated web-player-v0.2.0 client-auth pivot — device-login is removed.
 * The engine token is now minted by `POST /player/auth/select-venue`
 * (account → venue). Kept exported because `mintEngineSession` still consumes
 * the response shape via `playerSelectVenueResponseSchema`.
 */
export const playerLoginRequestSchema = z.object({
    venue_id: uuidSchema,
    login: z.string().min(1).max(128),
    password: z.string().min(1).max(256),
    device_fingerprint: z.string().min(1).max(255).optional(),
});
/**
 * `POST /player/login` response. Player JWTs are short-lived (15 min access /
 * 7 d refresh — see player auth service). Refresh token returned in the body
 * because mobile clients store tokens in the device keychain; there is no
 * httpOnly cookie surface for the mobile flow.
 *
 * @deprecated web-player-v0.2.0 client-auth pivot — the device-login route
 * that returned this shape is removed. The body shape lives on as the engine
 * session payload via `playerSelectVenueResponseSchema` (login `stage:'player'`
 * + select-venue + register reuse it). Marked deprecated as the *device-login*
 * response; the underlying field set is NOT deprecated.
 */
export const playerLoginResponseSchema = z.object({
    access_token: z.string().min(1),
    refresh_token: z.string().min(1),
    player_id: uuidSchema,
    venue_id: uuidSchema,
    account_id: uuidSchema,
    expires_at: isoDateTimeSchema,
});
/**
 * `POST /player/heartbeat` request body. `client_time` lets the backend
 * detect badly skewed clocks and is logged but never used for security
 * decisions (server clock wins for session expiry).
 *
 * `position_sec` (TASK-513, silent-venue detection) is the device's current
 * playback position in whole seconds of MEDIA time — `currentTime` of whichever
 * deck the room is actually hearing, NOT a wall clock and NOT a duration since
 * the track started. It exists so the server can answer one question the device
 * cannot be trusted to answer about itself: is a player that reports
 * `status='playing'` actually making progress, or is the room silent while the
 * client believes everything is fine? Every failure that has hurt this product
 * — an unsettled promise on an iPad, a transition state machine resting with
 * both decks muted (BUG-256) — leaves `status='playing'` intact and freezes
 * this number.
 *
 * REQUEST-SIDE ONLY, and deliberately so: every player RESPONSE schema is
 * `.strict()`, so a new field on one of those breaks parsing on any device
 * still served by an old service worker. Adding to the request cannot break an
 * old client — it simply never sends it.
 *
 * OPTIONAL, and the server treats "absent" as "this device tells me nothing
 * about its position", never as zero and never as healthy. That distinction is
 * the whole point: a detector that silently reports "all fine" when it is in
 * fact receiving nothing is the exact failure this feature exists to end.
 *
 * `intent` (STORY-023) — see {@link playerPlaybackIntentSchema}. Optional for
 * the same reason as `position_sec`: a bundle that predates it sends none, the
 * server stores NULL and the offline detector falls back to `status`. A new
 * optional request key is what ADR-042 rule 3 allows on this schema.
 */
export const playerHeartbeatRequestSchema = z.object({
    player_id: uuidSchema,
    current_track_id: uuidSchema.optional(),
    current_preset_id: uuidSchema.optional(),
    status: playerStatusSchema,
    client_time: isoDateTimeSchema,
    position_sec: z.number().int().nonnegative().max(86_400).optional(),
    intent: playerPlaybackIntentSchema.optional(),
});
export const playerHeartbeatResponseSchema = z.object({
    status: z.literal('ok'),
    server_time: isoDateTimeSchema,
});
/**
 * `POST /admin/players/:player_id/force-logout` admin action body. `reason`
 * is free-form text persisted in the audit log; max length matches the rest
 * of the audit changes payload conventions.
 */
export const playerForceLogoutRequestSchema = z.object({
    reason: z.string().min(1).max(500).optional(),
});
export const playerForceLogoutResponseSchema = z.object({
    status: z.literal('ok'),
    player_id: uuidSchema,
    was_active: z.boolean(),
});
// ---------------------------------------------------------------------------
// Wave 2.15 BE-2 (TASK-072b) — venue player state + history.
// Admin-only surface on the venue card (admin-prd §6.6 + §6.8 "Состояние
// плеера"). Distinct from the player-audience endpoints above: these are read
// models tailored for the venue card UI, with status derived server-side from
// `last_heartbeat_at` (no `offline` write on every tick).
// ---------------------------------------------------------------------------
/**
 * Derived venue-card status. `offline` is the server-derived bucket when
 * `last_heartbeat_at` is missing or older than the freshness threshold; the
 * other values are passed through from `customers.players.status`.
 */
export const venuePlayerDerivedStatusSchema = z.enum([
    'online',
    'offline',
    'idle',
    'paused',
    'playing',
]);
/**
 * Minimal track tuple surfaced on the venue card. The full track card is on
 * a separate route — here we only need a one-line "сейчас играет" label.
 */
export const venuePlayerTrackSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
});
/**
 * Active preset tuple. `presets` table is not in MVP yet; the field is
 * always `null` until TASK-080 ships the table (kept on the wire for
 * forward compatibility).
 */
export const venuePlayerPresetSchema = z.object({
    id: uuidSchema,
    name: z.string(),
});
/**
 * Active session tuple. `null` when the player is currently logged out
 * (no row in `internal.player_sessions` with `revoked_at IS NULL`).
 */
export const venuePlayerSessionSchema = z.object({
    id: uuidSchema,
    created_at: isoDateTimeSchema,
    device_fingerprint: z.string().nullable(),
});
/**
 * `GET /admin/venues/:id/player` response (admin-prd §6.6 + §6.8).
 *
 * `player_id` is `null` when no player row exists for this venue — the
 * endpoint still returns 200 so the FE can render an empty card without
 * an extra round-trip to distinguish 404 vs no-player.
 */
export const venuePlayerStateSchema = z
    .object({
    player_id: uuidSchema.nullable(),
    status: venuePlayerDerivedStatusSchema,
    last_heartbeat_at: isoDateTimeSchema.nullable(),
    current_track: venuePlayerTrackSchema.nullable(),
    current_preset: venuePlayerPresetSchema.nullable(),
    session: venuePlayerSessionSchema.nullable(),
    last_login_at: isoDateTimeSchema.nullable(),
})
    .strict();
/**
 * `GET /admin/venues/:id/history` query params (admin-prd §6.6 "история
 * последних 20 треков, 10 последних логинов"). Defaults match the PRD
 * counts; max bounds prevent expensive scans.
 */
export const venueHistoryQuerySchema = z
    .object({
    tracks_limit: z.coerce.number().int().min(1).max(50).default(20),
    logins_limit: z.coerce.number().int().min(1).max(30).default(10),
})
    .strict();
export const venueHistoryTrackSchema = z.object({
    played_at: isoDateTimeSchema,
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    duration_sec: z.number().int().nonnegative().nullable(),
});
export const venueHistoryLoginSchema = z.object({
    logged_in_at: isoDateTimeSchema,
    device_fingerprint: z.string().nullable(),
    session_id: uuidSchema,
    revoked_at: isoDateTimeSchema.optional(),
});
/**
 * `GET /admin/venues/:id/history` response. Tracks come from `content.
 * play_events` (created by TASK-080 — until then the array is always empty
 * and the response carries `tracks_source: 'unavailable'` so the FE can
 * surface the "история появится после TASK-080" placeholder). Logins come
 * from `internal.player_sessions`, scoped to the player rows of this venue.
 */
export const venueHistorySchema = z
    .object({
    tracks: z.array(venueHistoryTrackSchema),
    tracks_source: z.enum(['play_events', 'unavailable']),
    logins: z.array(venueHistoryLoginSchema),
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.21 BE-1 (TASK-067c) — PlaybackEngine wire shapes.
//
// `PlaybackEngineService.pickNextTrack` is an INTERNAL service in this wave
// (called only from in-process code; no HTTP). The schemas below are
// pre-published for TASK-081a (`POST /player/next-track`) — landing them
// now lets the FE share types and the BE pin a contract before the HTTP
// layer ships. `.strict()` everywhere so unknown keys are rejected at the
// boundary, matching every other schema in this file.
// ---------------------------------------------------------------------------
/**
 * Single point of the venue's BPM-by-hour curve. Mirrors the internal
 * `BPMCurvePoint` type in `apps/api/src/rotation/types.ts` — both shapes
 * are kept in sync deliberately.
 */
export const bpmCurvePointSchema = z
    .object({
    hour: z.number().int().min(0).max(23),
    bpm_min: z.number().int().positive(),
    bpm_max: z.number().int().positive(),
})
    .strict();
/**
 * Snapshot of the currently-playing track used for Camelot / mood scoring.
 * The player API stamps it from its session state — the engine never reads
 * it from the DB.
 */
export const currentTrackContextSchema = z
    .object({
    bpm: z.number().int().positive().nullable(),
    key_camelot: z.string().regex(/^([1-9]|1[0-2])[AB]$/).nullable(),
    mood_tag_ids: z.array(uuidSchema),
})
    .strict();
/**
 * `POST /player/next-track` (TASK-081a) request body. Fields mirror the
 * internal `PickNextInput` interface; the engine validates the same shape
 * on the call site.
 */
export const pickNextInputSchema = z
    .object({
    venue_id: uuidSchema,
    preset_version_id: uuidSchema,
    current_track_id: uuidSchema.optional(),
    exclude_recent_minutes: z.number().int().nonnegative().max(10080).optional(),
    bpm_curve: z.array(bpmCurvePointSchema),
    playback_settings: presetRulesSchema,
    current_dow: z.number().int().min(0).max(6),
    current_hour_min: localTimeSchema,
    current_track: currentTrackContextSchema.optional(),
    seed: z.number().int().optional(),
})
    .strict();
/**
 * Per-rule weight breakdown surfaced for audit / debug. Multiplicative
 * model (admin-arch §6.3) — final score is the product of all weights.
 */
export const rotationScoreBreakdownSchema = z
    .object({
    base_weight: z.number(),
    camelot_weight: z.number(),
    bpm_curve_weight: z.number(),
    mood_smoothing_weight: z.number(),
    priority_weight: z.number(),
})
    .strict();
/**
 * Scored candidate — wire shape. `has_explicit_lyrics` mirrors the
 * `tracks.has_explicit_lyrics` enum; `priority_weight` is the per-track
 * multiplier from `preset_priority_tracks` when present.
 */
export const scoredRotationCandidateSchema = z
    .object({
    track_id: uuidSchema,
    bpm: z.number().int().positive().nullable(),
    key_camelot: z.string().regex(/^([1-9]|1[0-2])[AB]$/).nullable(),
    mood_tag_ids: z.array(uuidSchema),
    has_explicit_lyrics: z.enum(['yes', 'no', 'unknown']),
    length_sec: z.number().int().nonnegative().nullable(),
    artist: z.string().nullable(),
    is_priority: z.boolean(),
    priority_weight: z.number().positive().nullable(),
    score: z.number(),
    score_breakdown: rotationScoreBreakdownSchema,
})
    .strict();
/**
 * Wave 2.25 BE-2 (TASK-067d) — cascade relaxation level. `0` is the happy
 * path; `1..5` means the engine had to soften filters because the strict
 * pool was empty. See `apps/api/src/rotation/types.ts` for level
 * semantics — kept in lock-step with the internal `RelaxationLevel` type.
 */
export const rotationRelaxationLevelSchema = z
    .union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
]);
/**
 * Decision metadata — small audit envelope. `explained_choice` is a
 * human-readable one-liner; the full breakdown lives on each scored
 * candidate above.
 *
 * Wave 2.25 BE-2 (TASK-067d) additions:
 *   - `relaxation_level` — cascade step the engine settled on (0..5).
 *   - `degraded_mode` — `true` whenever `relaxation_level > 0`.
 *   - `single_candidate` — `true` when only one candidate survived
 *     (no weighted random was needed).
 */
export const pickDecisionMetadataSchema = z
    .object({
    pool_size_after_hard_filters: z.number().int().nonnegative(),
    avg_score: z.number(),
    top_score: z.number(),
    bpm_curve_target_at_hour: z.number().nullable(),
    explained_choice: z.string(),
    relaxation_level: rotationRelaxationLevelSchema,
    degraded_mode: z.boolean(),
    single_candidate: z.boolean().optional(),
})
    .strict();
/**
 * `POST /player/next-track` (TASK-081a) response. `picked_track` is `null`
 * when the engine emitted `warning='preset_too_small'` AND the candidates
 * pool was empty — the caller (player API) decides whether to fall back
 * via cascade relaxation (TASK-067d).
 */
export const pickNextResultSchema = z
    .object({
    picked_track: scoredRotationCandidateSchema.nullable(),
    alternates: z.array(scoredRotationCandidateSchema),
    total_candidates: z.number().int().nonnegative(),
    decision_metadata: pickDecisionMetadataSchema,
    warning: z.enum(['preset_too_small']).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.22 BE-1 (TASK-081a + TASK-081b) — `/player/next-track` +
// `/player/event/*` wire schemas.
//
// `/player/next-track` is the player-facing entry to the rotation engine.
// The mobile player calls it whenever it needs the next track to enqueue,
// passing an optional snapshot of what is currently playing so the engine
// can apply key-aware / mood-smoothing rules. The player API resolves the
// active schedule slot, the venue's BPM curve + playback rules, and the
// preset's current version on the server side; the device does not need to
// know about presets or schedules.
//
// `/player/event/{started|finished|skipped}` writes to `content.play_events`.
// They are intentionally separate endpoints (one per action) to mirror the
// path table in admin-arch §3.12 — the path itself encodes the action so
// no client-supplied discriminant can be tampered with.
// ---------------------------------------------------------------------------
/**
 * `POST /player/next-track` request body.
 *
 * `current_track_id` + `current_track_started_at` let the engine apply the
 * anti-artist filter (PRD §4.6.3 rule 5) and the Camelot / mood-smoothing
 * scoring (rules 1, 4) against an actually-playing reference track. Both
 * are optional — on the very first call of a session there is no
 * predecessor.
 *
 * `seed` is reserved for the admin preset preview / deterministic-replay
 * flow (§4.6.5(д)) — the production player never sends it. Kept here so
 * one wire shape covers both call sites.
 *
 * `resume_track_id` (BUG-211, CEO decision 2026-07-13 — supersedes the
 * ADR-020 "rotation never resumes an exact track" rule): after a page
 * reload the device asks to CONTINUE the exact track it was playing. The
 * server returns that track (with fresh key/manifest tokens) when it is
 * still published and within the venue's tier window, otherwise it falls
 * back to a normal rotation pick — rotation stays authoritative.
 */
export const playerNextTrackRequestSchema = z
    .object({
    current_track_id: uuidSchema.optional(),
    current_track_started_at: isoDateTimeSchema.optional(),
    seed: z.number().int().optional(),
    resume_track_id: uuidSchema.optional(),
})
    .strict();
/**
 * Player-facing track tuple returned by `/player/next-track`. The mobile
 * client uses these fields to render the "now playing" card and to fetch
 * the encrypted HLS playlist. `hls_master_playlist_key` is the S3 object
 * key of the ABR master playlist (admin-arch §4.2) — the player resolves
 * it to a signed URL via `/player/key/...` in a follow-up wave
 * (TASK-081c / 081d).
 *
 * `track_version_id` is what the player attaches to subsequent
 * `/player/event/*` calls so the play_events row records the exact version
 * served (matters for analytics when a master is replaced under ADR-005).
 *
 * `hls_key_url` (Wave 2.23 BE-1, TASK-026 + TASK-027) is the relative URL
 * the HLS player hands to its key resolver — `/player/key/{track_id}/
 * {version_id}?token={jwt}`. The JWT is short-lived (5 min) and bound to
 * `aud='key-delivery'` with a SEPARATE secret from the player session JWT
 * so a leaked URL captured from a CDN log cannot replay the admin or player
 * surfaces. `null` when the picked track has no current version row (the
 * device falls back to silence / alternates).
 */
export const playerNextTrackPickedSchema = z
    .object({
    track_id: uuidSchema,
    track_version_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    length_sec: z.number().int().nonnegative().nullable(),
    hls_master_playlist_key: z.string().nullable(),
    hls_key_url: z.string().nullable(),
    // R1 (arch §E.1): ready manifest URL incl. `?token=` — FE loads verbatim,
    // never constructs it from track_id/version_id. Minted by play-manual too
    // (reuse). `null` when the picked track has no current version row.
    hls_manifest_url: z.string().nullable(),
    // E.5: cover art URL (public or presigned — see arch §E.5). Flows into
    // play-manual + alternates via reuse of this picked shape.
    cover_url: z.string().nullable(),
    bpm: z.number().int().positive().nullable(),
    key_camelot: z
        .string()
        .regex(/^([1-9]|1[0-2])[AB]$/)
        .nullable(),
    score: z.number(),
})
    .strict();
/**
 * Where the preset that decided the next track came from
 * (player-self-service-v0.1.0, TASK-368 / TASK-378). Resolution order in
 * `PlayerRotationService.nextTrack` is override → active slot → tier default;
 * the resolved branch is surfaced here so the FE `/now` view can label the
 * source ("по расписанию" / "тариф по умолчанию" / "вручную закреплено").
 *
 *   - `slot`           — an active schedule slot decided the preset;
 *   - `tariff_default` — no active slot, fell through to the tier default;
 *   - `override`       — a per-venue client override is pinned.
 *
 * `name` is the resolved preset name (`null` when the preset row exposes no
 * name — soft-deleted-preset / LEFT JOIN miss, mirroring `preset_name`).
 */
export const playerNextTrackSourceSchema = z
    .object({
    kind: z.enum(['slot', 'tariff_default', 'override']),
    name: z.string().nullable(),
})
    .strict();
/**
 * `POST /player/next-track` response. `picked_track` is `null` only when
 * the engine emitted `warning='preset_too_small'` AND the candidate pool
 * was empty (caller may retry via cascade relaxation in TASK-067d, but
 * v0.1.0 surfaces a 404 instead — see player-rotation service).
 *
 * `preset_id` / `preset_version_id` are echoed so the device can stamp
 * them onto subsequent `/player/event/*` calls without re-resolving the
 * schedule.
 */
export const playerNextTrackResponseSchema = z
    .object({
    track: playerNextTrackPickedSchema,
    alternates: z.array(playerNextTrackPickedSchema),
    preset_id: uuidSchema,
    preset_version_id: uuidSchema,
    // E.3: preset name lives on the RESPONSE (sibling of preset_id), NOT on
    // the picked track — otherwise it would be duplicated across `alternates`.
    // rotation → preset name; play-manual → null.
    preset_name: z.string().nullable(),
    // TASK-410: cover of the preset that decided this track — a presigned GET
    // (10-min TTL) for `content.presets.cover_s3_key`, rendered on the /now
    // screen next to «играет из набора «X»». Sibling of `preset_name` for the
    // same reason (one preset per response, not per picked track). `null` when
    // the preset has no cover, when the presigner failed (fail-safe), or on the
    // play-manual path — exactly like `preset_name`.
    preset_cover_url: z.string().nullable(),
    decision_metadata: pickDecisionMetadataSchema,
    // TASK-368: which branch (override / slot / tier default) decided the
    // preset. OPTIONAL + backward-compatible — the rotation path fills it; the
    // play-manual path leaves it `undefined`. Existing consumers ignore it.
    source: playerNextTrackSourceSchema.optional(),
    // Preset crossfade length (PRD §4.6.3 rule 8) — the device fades the
    // track edges over this many seconds. OPTIONAL for the same rolling-
    // deploy reason as `source`; absent = DEFAULT_CROSSFADE_SECONDS.
    crossfade_seconds: z.number().int().min(0).max(10).optional(),
    // DEPRECATED: never emitted on the wire in v0.1.0; empty-pool = HTTP 404 (NO_SCHEDULE_SLOT/NO_CANDIDATES/PRESET_NO_VERSION)
    warning: z.enum(['preset_too_small']).optional(),
})
    .strict();
/**
 * Reason a track stopped playing. Enum mirrors the CHECK constraint on
 * `content.play_events.reason_stopped` (migration 0024) plus the implicit
 * `null` value used while the track is still playing.
 *
 *  - `completed`       — natural end-of-track.
 *  - `skipped`         — explicit user skip.
 *  - `session_end`     — venue closed the player (no force-logout).
 *  - `force_logout`    — admin-initiated logout cut the session short.
 *  - `heartbeat_lost`  — gap > heartbeat window detected post-hoc by the
 *                       admin venue card; the player itself never sends
 *                       this value.
 */
export const playEventReasonStoppedSchema = z.enum([
    'completed',
    'skipped',
    'session_end',
    'force_logout',
    'heartbeat_lost',
]);
/**
 * Shared piece across the three /player/event/* request bodies. `preset_id`
 * + `preset_version_id` come straight from the matching `/player/next-track`
 * response — the player MUST echo them so analytics can join an event to
 * the preset version that decided it.
 *
 * `client_time_offset_ms` is the player's clock drift vs the server clock
 * as observed on `/player/heartbeat`; it is stored verbatim so analytics
 * can subtract it from raw `started_at` if needed. Default 0 keeps the
 * column non-null without forcing every client to compute drift.
 */
const playerEventBaseShape = {
    track_id: uuidSchema,
    track_version_id: uuidSchema,
    preset_id: uuidSchema,
    preset_version_id: uuidSchema,
    started_at: isoDateTimeSchema,
    client_time_offset_ms: z.number().int().min(-86_400_000).max(86_400_000).optional(),
};
/**
 * `POST /player/event/started` body. Fired the moment the player first
 * pushes bytes into the audio output. `started_at` is the device-local
 * wall-clock; the backend trusts it (this is the player's session) but
 * stamps the row's `created_at` from `NOW()` so the audit trail still has
 * a server-clock anchor.
 */
export const playerEventStartedRequestSchema = z
    .object(playerEventBaseShape)
    .strict();
/**
 * `POST /player/event/finished` body. `duration_played_ms` is the
 * player-reported elapsed audio time (NOT wall-clock — pauses do not
 * count). `reason_stopped` defaults to `'completed'` and is otherwise
 * passed through; the player surfaces this enum on its session-end /
 * heartbeat-lost paths.
 */
export const playerEventFinishedRequestSchema = z
    .object({
    ...playerEventBaseShape,
    ended_at: isoDateTimeSchema,
    duration_played_ms: z.number().int().nonnegative().max(86_400_000),
    reason_stopped: z
        .enum(['completed', 'session_end', 'force_logout', 'heartbeat_lost'])
        .default('completed'),
})
    .strict();
/**
 * `POST /player/event/skipped` body. Distinct from `/finished` so the
 * controller can stamp `reason_stopped='skipped'` server-side regardless
 * of what the player puts on the wire — keeps the analytics enum honest.
 */
export const playerEventSkippedRequestSchema = z
    .object({
    ...playerEventBaseShape,
    ended_at: isoDateTimeSchema,
    duration_played_ms: z.number().int().nonnegative().max(86_400_000),
})
    .strict();
/**
 * Shared response across all three /player/event/* endpoints. `event_id`
 * is the freshly inserted `content.play_events.id` (UUIDv7); useful for
 * client-side dedupe on retried POSTs.
 */
export const playerEventResponseSchema = z
    .object({
    event_id: uuidSchema,
    recorded_at: isoDateTimeSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.23 BE-1 (TASK-026 + TASK-027) — HLS key-delivery JWT payload.
//
// Published here so the FE / player can share the verified shape for type
// safety and so test fixtures stay in sync with the BE definition. The token
// is minted by `KeyDeliveryService.generateKeyJwt` and verified by
// `KeyJwtAuthGuard` against `JWT_KEY_DELIVERY_SECRET` (separate from admin /
// player session secrets). The plaintext DEK is NEVER in the token — the
// endpoint looks it up via `(track_id, version_id)` and unwraps server-side.
// ---------------------------------------------------------------------------
/**
 * `aud` claim is a constant; exported as a value so consumers can use it in
 * `verifyAsync({ audience })` without typing the literal string twice.
 */
export const KEY_DELIVERY_TOKEN_AUDIENCE = 'key-delivery';
export const keyJwtPayloadSchema = z
    .object({
    aud: z.literal(KEY_DELIVERY_TOKEN_AUDIENCE),
    track_id: uuidSchema,
    version_id: uuidSchema,
    account_id: uuidSchema,
    player_id: uuidSchema,
    iat: z.number().int().nonnegative().optional(),
    exp: z.number().int().nonnegative().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.24 BE-1 (TASK-081c + TASK-081d) — player likes / dislikes / manual
// play / catalog search.
//
// All endpoints are gated by `@PlayerAuth()`. `like`/`dislike`/`likes` work on
// every tariff; `play-manual` is Pro-only (403 TARIFF_INSUFFICIENT for
// Lite/Standard); `catalog/search` is tier-aware (Lite sees `lite` tracks,
// Standard sees `lite|standard`, Pro sees the whole catalog).
//
// Persistence pattern mirrors Wave 2.1 BE: a partial unique index on
// `(venue_id, track_id_placeholder) WHERE removed_at IS NULL` keeps exactly
// one active row per (venue, track). Toggling restores by clearing
// `removed_at` rather than INSERTing a fresh row (history retention).
// ---------------------------------------------------------------------------
/**
 * Dislike reason vocabulary. Brief takes precedence over the legacy free-form
 * text in migration 0004 (the column has no DB-level CHECK, so the
 * application enforces the closed set here). Reasons are:
 *
 *   - `inappropriate` — not fit for the venue (genre / vibe mismatch);
 *   - `duplicate`    — already heard recently / same track different track id;
 *   - `low_quality`  — audio quality complaint (encoding / mastering);
 *   - `other`        — anything else (free-form notes deferred to v0.2).
 */
export const playerDislikeReasonSchema = z.enum([
    'inappropriate',
    'duplicate',
    'low_quality',
    'other',
]);
/**
 * `POST /player/like` request body. Sole field is the track to favourite. The
 * venue is taken from `req.player.venue_id` — the player JWT scopes the
 * action, so the device never sends a venue id.
 */
export const playerLikeRequestSchema = z
    .object({
    track_id: uuidSchema,
})
    .strict();
/**
 * `POST /player/like` response. `liked_at` is the canonical favourite
 * timestamp — on a fresh INSERT it is `now()`; on a restore (the row existed
 * but was tombstoned via DELETE /player/like/:track_id) we re-stamp
 * `liked_at` to `now()` so the FE-sortable "recently liked" view stays
 * intuitive.
 */
export const playerLikeResponseSchema = z
    .object({
    liked_at: isoDateTimeSchema,
})
    .strict();
/**
 * `POST /player/dislike` request body. Same venue-scoping rules as
 * `playerLikeRequestSchema`; `reason` is required (PRD §9.5 records the
 * reason for the admin "common dislikes" view).
 */
export const playerDislikeRequestSchema = z
    .object({
    track_id: uuidSchema,
    reason: playerDislikeReasonSchema,
})
    .strict();
/**
 * `POST /player/dislike` response. `current_count` is the post-write count
 * of active dislikes for the venue — the device renders the "много
 * заблокированных треков" banner when `current_count > 100` (PRD §9.5).
 * The response also carries `X-Dislike-Limit-Warning: true` as an HTTP
 * header for non-JSON clients (e.g. log-only telemetry).
 */
export const playerDislikeResponseSchema = z
    .object({
    disliked_at: isoDateTimeSchema,
    current_count: z.number().int().nonnegative(),
})
    .strict();
/**
 * Favourite track tuple surfaced by `GET /player/likes`. Subset of the
 * tracks columns the player needs to render the "Мои треки" list. The
 * favourites flow does NOT include the encrypted HLS / key URL — those are
 * minted on `/player/play-manual` when the user actually picks a track to
 * play, matching the Lite-tier "no full catalog" constraint (PRD §9.4).
 */
export const playerLikedTrackSchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    length_sec: z.number().int().nonnegative().nullable(),
    liked_at: isoDateTimeSchema,
    // E.5: cover + BPM/Camelot so Likes can render the same mono-chips as
    // Search (design §3.2.4). nullable/reversible — if the chips are dropped
    // from Likes the FE simply won't render them. Strict forms (positive bpm +
    // Camelot regex) to match playerCatalogTrackSchema / playerNextTrackPickedSchema
    // — review fix, no reason Likes should validate looser than Search/picked.
    cover_url: z.string().nullable(),
    bpm: z.number().int().positive().nullable(),
    key_camelot: z
        .string()
        .regex(/^([1-9]|1[0-2])[AB]$/)
        .nullable(),
})
    .strict();
/**
 * `GET /player/likes` response. No pagination — favourites are bounded by
 * the venue's own activity (admin-prd §9.5 explicitly: "без лимита",
 * but in practice the active-rows partial index keeps this query small).
 * The response carries `total` so the FE can render a count without a
 * second round-trip.
 */
export const playerLikesResponseSchema = z
    .object({
    items: z.array(playerLikedTrackSchema),
    total: z.number().int().nonnegative(),
})
    .strict();
/**
 * `GET /player/dislikes` response (STORY-072, ios-app-arch §9.5): the tracks
 * the venue disliked and has not undone, newest first, for any live session of
 * the venue. No paging — a venue's dislikes run to hundreds (the web warns
 * above 100), about 40 bytes each. The app marks a search or preset row whose
 * `track_id` is listed «Не подходит»: the frozen search and preset-track
 * responses cannot carry that key (ADR-038 rule 1). Frozen by ADR-042 rule 3.
 */
export const playerDislikesResponseSchema = z
    .object({
    track_ids: z.array(uuidSchema),
})
    .strict();
/**
 * `GET /player/catalog/search` query string. `limit` is bounded so a Pro
 * device cannot scan the catalog with `?limit=10000`. `q` minimum length of
 * 2 keeps the trgm-supported `ILIKE %q%` predicate cheap; shorter queries
 * fall through to a 400 (zod validation) rather than scanning the table.
 */
export const playerCatalogSearchQuerySchema = z
    .object({
    q: z.string().trim().min(2).max(120),
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .strict();
/**
 * Single catalog-search hit. `tier_access` is surfaced so the device can
 * grey-out tracks that fall outside the venue's tariff window — the server
 * already filters tier-aware, but echoing the value keeps the FE
 * defensively idempotent (a UI bug that drops the filter cannot accidentally
 * play a Pro-only track on a Lite venue).
 */
export const playerCatalogTrackSchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    length_sec: z.number().int().nonnegative().nullable(),
    bpm: z.number().int().positive().nullable(),
    key_camelot: z
        .string()
        .regex(/^([1-9]|1[0-2])[AB]$/)
        .nullable(),
    // E.5: cover art URL (public or presigned — see arch §E.5).
    cover_url: z.string().nullable(),
    tier_access: z.enum(['lite', 'standard', 'pro']),
})
    .strict();
/**
 * `GET /player/catalog/search` response. `items` is up to `limit` rows;
 * `total` is the unbounded count for the same predicate so the FE can
 * render "N результатов" / hide pagination when not needed.
 */
export const playerCatalogSearchResponseSchema = z
    .object({
    items: z.array(playerCatalogTrackSchema),
    total: z.number().int().nonnegative(),
})
    .strict();
// ---------------------------------------------------------------------------
// web-player v0.1.0 (arch §E.0, ADR-011/012/013/014) — the 6 player additions.
// Schema-only contract: endpoint logic lands in TASK-308…313. New fields are
// nullable so a not-yet-updated mapping branch never throws. All `.strict()`.
// ---------------------------------------------------------------------------
/**
 * `POST /player/refresh` request body (arch §E.2 / ADR-011). The response
 * REUSES `playerLoginResponseSchema` verbatim (new access + rotated refresh +
 * identity), so no separate response schema is declared here.
 */
export const playerRefreshRequestSchema = z
    .object({ refresh_token: z.string().min(1) })
    .strict();
export const playerRefreshResponseSchema = playerLoginResponseSchema;
/**
 * `GET /player/me` response (arch §E.4). `subscription_status` is a DERIVED UI
 * bucket, NOT the raw DB status: `active`/`trial` → `'active'`, near-expiry /
 * grace → `'warning'`, suspended / past-due-beyond-grace → `'blocked'`. There
 * is no `paused` status in the code.
 *
 * player-ux-wave2 additions:
 *   - `venues_count` — number of venues the account owns. The FE hides the
 *     «Сменить заведение» control when this is `1` (single-venue account).
 *   - `email` — contact email of the account's earliest-joined active client
 *     member (owner/manager), shown in the Кабинет info block and echoed in the
 *     «Подтвердите почту» banner. `null` when the account has no resolvable
 *     member row. Kept consistent with the resend target in
 *     `PlayerEmailVerificationService.loadAccountMemberToVerify`.
 *   - `email_verified` — soft email-verification flag (TASK-389). Drives the
 *     non-blocking «Подтвердите почту» banner; gates NOTHING.
 *   - `venue_address` — venue address shown/prefilled in the Кабинет venue-info
 *     edit form (`PATCH /player/venue`). `null` when unset.
 */
export const playerMeResponseSchema = z
    .object({
    player_id: uuidSchema,
    venue_id: uuidSchema,
    account_id: uuidSchema,
    venue_name: z.string().nullable(),
    venue_address: z.string().nullable(),
    venues_count: z.number().int().min(1),
    tariff: z.enum(['lite', 'standard', 'pro']).nullable(),
    subscription_status: z.enum(['active', 'warning', 'blocked']),
    period_end: isoDateTimeSchema.nullable(),
    grace_expires_at: isoDateTimeSchema.nullable(),
    email: z.string().nullable(),
    email_verified: z.boolean(),
})
    .strict();
/**
 * Single schedule slot surfaced by `GET /player/schedule` (arch §E.6).
 * `start_local_time` is `'HH:MM'` (BE normalizes via `to_char(..., 'HH24:MI')`).
 * `preset_name` is nullable for the soft-deleted-preset case (LEFT JOIN miss).
 */
/**
 * One preset in a part's ordered sequence, surfaced by `GET /player/schedule`
 * (ADR-027 §3, schedule-multi-preset). `duration_minutes` is a 15-min multiple
 * (≥15) or `null` («до конца слота» — legal on the last item only).
 * `is_active_now` marks which item is the current rotation source for the part
 * that owns the active slot. `preset_name` is nullable for the soft-deleted
 * preset case (LEFT JOIN miss).
 */
export const playerScheduleItemSchema = z
    .object({
    preset_id: uuidSchema,
    preset_name: z.string().nullable(),
    // TASK-411: artwork of the preset `preset_name` names — a presigned GET
    // (10-min TTL) for `content.presets.cover_s3_key`. Sibling of
    // `preset_name`, and `null` in exactly the cases the name is unusable:
    // preset has no cover, or the presigner failed (fail-safe).
    preset_cover_url: z.string().nullable(),
    duration_minutes: z.number().int().nullable(),
    is_active_now: z.boolean(),
})
    .strict();
/**
 * ADR-030: the play window of this part — computed by the SERVER, rendered
 * verbatim by the FE, NEVER recomputed client-side (the doctrine of the admin
 * `computed_end_local_time`, `schemas/schedule.ts`). Absent ONLY on API
 * responses predating TASK-404 (additive, ADR-030 §5.2); when present it is
 * ALWAYS valid and of non-zero length — hence `.optional()`, never
 * `.nullable()` (ADR-030 §1.1). An empty part has no entry at all, so there is
 * no window to place.
 *
 * NOT named `computed_end_local_time` (ADR-030 §1.4): the admin field caps the
 * day's last slot at `'23:59:59'` and never wraps, while this one is the
 * rotation arc — it wraps past midnight and knows about 24/7. One name for two
 * semantics would open the very drift class this closes.
 */
export const playerSchedulePlayWindowSchema = z
    .object({
    /** Window end, wall clock, ALREADY modulo the day (`'HH:MM'`, 00:00–23:59).
     *  Rendered verbatim. Midnight is always `'00:00'` — `'24:00'` does not
     *  exist on the wire (`localTimeSchema` rejects it), ADR-030 §1.3. */
    end_local_time: localTimeSchema,
    /** Window length in minutes, 1..1440. The ONLY 24/7 disambiguator
     *  (ADR-030 §1.2) and the fill meter's denominator: a lone «Вечер 18:00»
     *  anchor playing 24/7 and a zero-length window BOTH serialize their end as
     *  `'18:00'`, and no FE computation can tell them apart without this. */
    duration_minutes: z.number().int().min(1).max(1440),
    /** The window end falls in the next calendar day (`start + duration > 1440`).
     *  Drives the «🌙 через полночь» marker and the two-slice day strip. */
    crosses_midnight: z.boolean(),
    /** The part plays round the clock (`duration_minutes === 1440`). Rendered as
     *  «круглосуточно», NEVER as a range (ADR-030 §1.2). Formally redundant, and
     *  deliberately so: the goal is that the FE keeps NOT ONE condition about
     *  time semantics. Both flags are computed atomically by the same helper, so
     *  they cannot disagree with each other. */
    is_all_day: z.boolean(),
})
    .strict();
export const playerScheduleEntrySchema = z
    .object({
    slot_id: uuidSchema,
    day_of_week: z.number().int().min(0).max(6).nullable(),
    start_local_time: localTimeSchema,
    preset_id: uuidSchema,
    preset_name: z.string().nullable(),
    // TASK-411: artwork of the preset the top-level `preset_id` / `preset_name`
    // describe — i.e. the ACTIVE item's preset on the active entry, else the
    // position-0 preset. Presigned GET (10-min TTL), `null` when that preset
    // has no cover or the presigner failed. Per-item artwork lives on
    // `items[].preset_cover_url`.
    preset_cover_url: z.string().nullable(),
    is_active_now: z.boolean(),
    // ADR-027 §3: the ordered preset sequence for this part. Optional so a
    // not-yet-updated FE keeps working off the legacy top-level `preset_id` /
    // `preset_name` (populated with the currently-active item). Single-preset
    // parts carry exactly one item.
    items: z.array(playerScheduleItemSchema).optional(),
    // ADR-028 (free day-part boundaries): the PERSISTED part label of a daily
    // self-service slot. `null` for admin per-weekday rows and legacy daily
    // rows not yet re-saved (the FE falls back to the old hour-window
    // bucketing for those). Additive + optional so older readers keep
    // parsing. Inline enum (not `schedulePartSchema`) because that const is
    // declared later in this module (TDZ).
    part: z.enum(['morning', 'day', 'evening']).nullable().optional(),
    // ADR-030: the server-computed play window of this part. Additive +
    // optional so a legacy response (pre-TASK-404) keeps parsing; the FE
    // renders it verbatim and derives NOTHING about time from the entry set.
    play_window: playerSchedulePlayWindowSchema.optional(),
})
    .strict();
/**
 * BUG-219 / ADR-029 §3: the forward-fill SOURCE of each EMPTY day-part, keyed by
 * the three fixed parts. An empty part has no anchor of its own, so (ADR-030
 * §1.1) it has NO entry in `entries[]` — the source cannot live on an entry and
 * lives here, read by part key.
 *
 * Under the circle (ADR-029) an empty part is voiced by a neighbour: the most
 * recent earlier anchor (forward-fill) or, when nothing has started yet, the
 * day's last daily anchor wrapping in from «yesterday» (Step A′). For each part
 * the value is that sourcing `SchedulePart`, so the FE can render «Продолжает
 * играть „Утро"» instead of the source-agnostic «Играет поток по умолчанию».
 *
 * `null` in the two cases where no neighbour voices the part: it has its OWN
 * anchor (not empty), OR the whole schedule is empty (nothing sources it →
 * tier-default, where today's «Играет поток по умолчанию» is the correct copy).
 * The FE already knows which parts are assigned (entry presence), so it only
 * consults this for a part it renders empty.
 *
 * The value is a PART KEY, not a localized label: the FE maps it to its own
 * «Утро»/«День»/«Вечер», consistent with `part` and `schedulePartSchema`. Inline
 * enum (not `schedulePartSchema`, declared later — TDZ), matching the `part`
 * field above.
 */
const emptyPartSourceValueSchema = z
    .enum(['morning', 'day', 'evening'])
    .nullable();
export const playerScheduleEmptyPartSourcesSchema = z
    .object({
    morning: emptyPartSourceValueSchema,
    day: emptyPartSourceValueSchema,
    evening: emptyPartSourceValueSchema,
})
    .strict();
/**
 * `GET /player/schedule` response (arch §E.6). `server_now_local` is the venue
 * local clock in `'HH:MM'`; `active_slot_id` matches the `slot_id` of the
 * currently-active entry (`null` if no slot is active now).
 */
export const playerScheduleResponseSchema = z
    .object({
    timezone: z.string(),
    server_now_local: localTimeSchema,
    current_dow: z.number().int().min(0).max(6),
    active_slot_id: uuidSchema.nullable(),
    entries: z.array(playerScheduleEntrySchema),
    // BUG-219: which part forward-fills each EMPTY day-part (see schema above).
    // Additive + optional — a legacy response (pre-BUG-219) still parses. The
    // server always emits it; the FE renders «Продолжает играть „{label}"» from
    // it on an empty card.
    empty_part_sources: playerScheduleEmptyPartSourcesSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// web-player-v0.2.0 — client-account auth pivot (arch §9.1, ADR-015/016/017).
//
// The player moves from device-login (`POST /player/login`, deprecated above)
// to client-account login against `internal.users`, gated by an active
// `customers.account_users` client membership. The engine token stays
// `aud='player'` (byte-identical to today). A short-lived `aud='player-account'`
// stage token bridges login → venue-pick → engine-session for multi-venue
// accounts. All schemas `.strict()`.
// ---------------------------------------------------------------------------
/**
 * `POST /player/auth/register` request body (arch §4). Minimal self-service
 * registration: identity + the first venue name. Trial subscription, logical
 * player, and a royalty-free default schedule slot are provisioned server-side
 * in one transaction. `password` minimum mirrors the platform policy used by
 * admin invite / password-reset (>= 8 chars).
 */
export const playerRegisterRequestSchema = z
    .object({
    email: z.string().trim().email().max(255),
    password: z.string().min(8).max(256),
    /**
     * OPTIONAL since CEO 2026-09-01: the sign-up form asked for it and the
     * cabinet asks for it again, and a name typed before the operator has seen
     * anything is the one they change first. Absent → the venue is created
     * WITHOUT a name and «Кабинет» is where it gets one. Still accepted, so a
     * client that sends it keeps working.
     */
    venue_name: z.string().trim().min(1).max(120).optional(),
})
    .strict();
/**
 * `POST /player/auth/login` request body (arch §7.2). Email + password against
 * `internal.users`; membership gate + outcome (engine session vs picker) are
 * resolved server-side.
 */
export const playerAccountLoginRequestSchema = z
    .object({
    email: z.string().trim().email().max(255),
    password: z.string().min(1).max(256),
})
    .strict();
/**
 * Engine-session payload (arch §7.1/§7.4). The `aud='player'` access + refresh
 * pair plus the venue/account/player coordinates the engine consumes. Field set
 * is identical to the (deprecated) device-login response — `mintEngineSession`
 * produces exactly this shape so every existing `/player/*` endpoint stays
 * byte-compatible. Reused by login `stage:'player'`, select-venue, and register.
 */
export const playerSelectVenueResponseSchema = playerLoginResponseSchema;
/**
 * Derived subscription bucket surfaced in the venue picker. Same vocabulary as
 * `playerMeResponseSchema.subscription_status` (`active`/`warning`/`blocked`).
 */
export const playerVenueSummarySchema = z
    .object({
    venue_id: uuidSchema,
    name: z.string(),
    tariff: z.enum(['lite', 'standard', 'pro']).nullable(),
    subscription_status: z.enum(['active', 'warning', 'blocked']),
})
    .strict();
/**
 * `GET /player/auth/venues` response (arch §7.3) — the picker payload behind the
 * `aud='player-account'` stage guard. Lets the FE re-render the picker while the
 * stage token is valid without a re-login.
 */
export const playerVenueListResponseSchema = z
    .object({
    venues: z.array(playerVenueSummarySchema),
})
    .strict();
/**
 * `POST /player/auth/login` response (arch §7.2) — discriminated union on
 * `stage`:
 *   * `'player'`       — exactly one active-membership venue → engine session
 *     minted server-side (auto-select). Carries the full engine token payload.
 *   * `'select_venue'` — more than one venue → a 10-min `aud='player-account'`
 *     stage token + the venue picker list. The FE exchanges the stage token at
 *     `POST /player/auth/select-venue` for the engine session.
 *   * `'onboarding'`   — the user is a valid active client member of an account
 *     that has NO venue yet (BUG-177). Carries the same 10-min stage token so
 *     the FE can call `POST /player/venues` to create the first «заведение»,
 *     then receives the engine session directly. `account_id` is the account
 *     the venue will be created under.
 */
export const playerLoginOutcomeSchema = z.discriminatedUnion('stage', [
    playerSelectVenueResponseSchema.extend({ stage: z.literal('player') }).strict(),
    z
        .object({
        stage: z.literal('select_venue'),
        account_token: z.string().min(1),
        venues: z.array(playerVenueSummarySchema),
    })
        .strict(),
    z
        .object({
        stage: z.literal('onboarding'),
        account_token: z.string().min(1),
        account_id: uuidSchema,
    })
        .strict(),
]);
/**
 * `POST /player/venues` request body (BUG-177). Self-service creation of the
 * first «заведение» for an onboarding owner (account with 0 venues). Guarded by
 * the `aud='player-account'` stage token; `name` mirrors the register flow's
 * `venue_name` constraints. One account = one venue for now — a second call
 * against an account that already has a venue is rejected with 409.
 */
export const playerCreateVenueRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
})
    .strict();
/**
 * `POST /player/venues` response (BUG-177). On success the first venue is
 * provisioned (venue + logical player + royalty-free default schedule slot)
 * and the engine session is minted immediately — same shape as select-venue /
 * register so the FE transitions straight into the player.
 */
export const playerCreateVenueResponseSchema = playerSelectVenueResponseSchema;
/**
 * `PATCH /player/venue` request body (Кабинет venue-info edit). Engine token
 * (`@PlayerAuth()`, any tariff); the target venue always comes from the JWT —
 * no venue id on the wire. Only the safe display fields are exposed: `name`
 * (required, mirrors the register/create constraints) and `address`
 * (optional; `null` or blank clears it, omitted leaves it unchanged).
 * Rotation-affecting fields (timezone, working_hours, bpm) are admin-only.
 */
export const playerVenueUpdateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    address: z.string().trim().max(500).nullable().optional(),
})
    .strict();
/** `PATCH /player/venue` response — the updated display fields. */
export const playerVenueUpdateResponseSchema = z
    .object({
    venue_id: uuidSchema,
    name: z.string(),
    address: z.string().nullable(),
})
    .strict();
/**
 * `POST /player/auth/select-venue` request body (arch §7.4). The venue must
 * belong to an account the authenticated user is an active member of — the
 * service validates this before minting the engine session.
 */
export const playerSelectVenueRequestSchema = z
    .object({
    venue_id: uuidSchema,
})
    .strict();
/**
 * `POST /player/auth/logout` response (arch §7.5). Idempotent — revokes the
 * current player session and NULLs `players.current_session_id`.
 */
export const playerLogoutResponseSchema = z
    .object({ status: z.literal('ok') })
    .strict();
/**
 * `POST /player/auth/change-password` request body (ADR-019, TASK-388).
 *
 * In-session, on-device credential rotation from the player Account screen (no
 * email round-trip). `current_password` is verified (bcrypt anti-timing) and
 * used to resolve the internal.users row behind the account membership — the
 * engine token does not carry user_id. `new_password >= 8` mirrors the platform
 * policy (register / admin invite); `new != current` is enforced server-side.
 */
export const playerChangePasswordRequestSchema = z
    .object({
    current_password: z.string().min(1).max(256),
    new_password: z.string().min(8).max(256),
})
    .strict();
/**
 * `POST /player/auth/change-password` response (ADR-019).
 *
 * BUG-237: the rotation now revokes EVERY player session of the principal,
 * the caller's included — the threat is a stolen token, and a thief refreshing
 * out of the kiosk's own storage shares the caller's session row, so sparing
 * it would spare him. The caller is handed a FRESH engine session in the same
 * response instead, so the kiosk keeps playing without a manual re-login.
 *
 * The session fields are OPTIONAL only because a non-kiosk caller could exist
 * later; the engine-token route always returns them.
 *
 * DEPLOY ORDERING — PLAYER BUNDLE FIRST, THEN THE API. This schema is
 * `.strict()`, so an OLD bundle does not "degrade gracefully" on the six added
 * keys: `apps/player/src/api/auth.ts` calls `.parse(raw)`, which THROWS, so
 * `applySession` never runs and the operator is shown a failure for an
 * operation that SUCCEEDED. They will then try the old password — which no
 * longer works — while their session is already revoked, so the kiosk goes
 * silent and needs an on-site login with the new password. The new bundle
 * accepts the response with or without the session fields, which is what makes
 * bundle-first safe and API-first not.
 *
 * The exposure outlives the deploy by however long a tab keeps running the old
 * JS: the service worker is `registerType: 'autoUpdate'` with NetworkFirst
 * navigations, so a reload picks the new shell up, but a kiosk tab that never
 * navigates will not. Announce a hard refresh with the release — this project
 * has already been bitten by an additive field breaking a stale service worker.
 */
export const playerChangePasswordResponseSchema = z
    .object({
    status: z.literal('ok'),
    access_token: z.string().min(1).optional(),
    refresh_token: z.string().min(1).optional(),
    player_id: uuidSchema.optional(),
    venue_id: uuidSchema.optional(),
    account_id: uuidSchema.optional(),
    expires_at: isoDateTimeSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// player-ux-wave2 — in-session venue switch (arch §7.4 sibling of select-venue).
//
// `POST /player/auth/switch-venue` is authenticated by the ENGINE token
// (`aud='player'`), NOT the stage token: an already-signed-in kiosk swaps to a
// sibling venue of the SAME account without a re-login. The target venue's
// account membership is re-derived from the DB (the token is not trusted for
// scope); the engine session is re-minted for the target venue and the prior
// session rotated per single-active-session semantics.
// ---------------------------------------------------------------------------
/**
 * `POST /player/auth/switch-venue` request body. `venue_id` must belong to an
 * account the current player's user is an ACTIVE member of — validated
 * server-side before the engine session is re-minted (403 otherwise).
 */
export const playerSwitchVenueRequestSchema = z
    .object({
    venue_id: uuidSchema,
})
    .strict();
/**
 * `POST /player/auth/switch-venue` response — the fresh engine session bound to
 * the target venue. Same shape as select-venue / register so the FE swaps the
 * stored token and continues playing.
 */
export const playerSwitchVenueResponseSchema = playerSelectVenueResponseSchema;
// ---------------------------------------------------------------------------
// player-ux-wave2 — soft email verification (TASK-389, non-blocking).
//
// Registration stays instant; a single-use expiring token is emailed and the
// `internal.users.email_verified_at` column is stamped on click. Gates nothing
// — the FE shows a dismissible banner while `email_verified` is false.
// ---------------------------------------------------------------------------
/**
 * `POST /player/auth/resend-verification` response. Engine-token authenticated,
 * throttled like login (5/60s). Anti-enumeration: always `{status:'ok'}` even
 * when the email is already verified (no new mail sent in that case).
 */
export const playerResendVerificationResponseSchema = z
    .object({ status: z.literal('ok') })
    .strict();
/**
 * Claims of the `aud='player-account'` stage token (arch §9.2). Signed with the
 * existing `JWT_PLAYER_ACCESS_SECRET`; the `aud` is the separator (no new
 * secret). Exported for test fixtures + guard cross-checks.
 */
export const playerAccountTokenClaimsSchema = z
    .object({
    sub: uuidSchema,
    aud: z.literal('player-account'),
    account_ids: z.array(uuidSchema),
})
    .strict();
// ---------------------------------------------------------------------------
// player-self-service-v0.1.0 (arch §2.4 + §3.2, TASK-361) — schedule-part
// self-service. Standard/Pro accounts edit a fixed three-part day grid
// (morning / day / evening) where each part maps a `start_local_time` to a
// preset. The wire shape is snake_case to stay byte-compatible with the BE
// row mapping and the FE form — single source of truth, no wire drift.
// All `.strict()`.
// ---------------------------------------------------------------------------
/**
 * The three fixed parts of the self-service day grid (arch §2.4). The window
 * boundaries themselves are derived from each part's `start_local_time`; the
 * enum only names the slot the value belongs to.
 */
export const schedulePartSchema = z.enum(['morning', 'day', 'evening']);
/**
 * One item in a `PUT /player/schedule/parts/:part` request (ADR-027 §3, D1/D4).
 * `duration_minutes` is a 15-min multiple (≥15) OR `null` («до конца слота»).
 * The "null only on the LAST item" rule is a cross-field refine on the parent
 * upsert schema (a per-item refine cannot see its position). `preset_id` must
 * reference a preset the venue is allowed to use (validated server-side against
 * curated + tier access, per item).
 */
export const playerSchedulePartUpsertItemSchema = z
    .object({
    preset_id: uuidSchema,
    duration_minutes: z
        .number()
        .int()
        .min(15)
        .multipleOf(15)
        .nullable(),
})
    .strict();
/**
 * `PUT /player/schedule/parts/:part` request body (ADR-027 §3,
 * schedule-multi-preset). Two accepted shapes for a transition window:
 *
 *   * v2 (multi-preset): `{ start_local_time, items: [...] }` — the ordered
 *     sequence, 1..5 items (D1). Only the LAST item may carry
 *     `duration_minutes: null` («до конца слота», D5). Repeats allowed (D7).
 *   * legacy (single-preset): `{ start_local_time, preset_id }` — mapped by the
 *     BE to `items: [{ preset_id, duration_minutes: null }]` so un-updated
 *     clients keep working.
 *
 * `start_local_time` is the part-anchor boundary and stays part-window
 * constrained (BUG-198 write guard, unchanged).
 */
export const playerSchedulePartUpsertSchema = z.union([
    z
        .object({
        start_local_time: localTimeSchema,
        items: z
            .array(playerSchedulePartUpsertItemSchema)
            .min(1, 'Нужна хотя бы одна подборка')
            .max(5, 'Не более 5 подборок в одной части дня'),
    })
        .strict()
        .refine((v) => v.items.every((it, i) => it.duration_minutes !== null || i === v.items.length - 1), {
        message: '«До конца слота» допускается только у последней подборки в списке',
        path: ['items'],
    }),
    z
        .object({
        start_local_time: localTimeSchema,
        preset_id: uuidSchema,
    })
        .strict(),
]);
/**
 * One item echoed in the `PUT /player/schedule/parts/:part` response
 * (ADR-027 §3). `preset_name` is nullable for the soft-deleted-preset case
 * (LEFT JOIN miss), mirroring `playerScheduleItemSchema`.
 */
export const playerSchedulePartResponseItemSchema = z
    .object({
    preset_id: uuidSchema,
    preset_name: z.string().nullable(),
    duration_minutes: z.number().int().nullable(),
})
    .strict();
/**
 * Response of `PUT /player/schedule/parts/:part` (ADR-027 §3). Returns the
 * part, the anchor `slot_id`, `start_local_time`, and the persisted ordered
 * `items[]` in position order.
 */
export const playerSchedulePartResponseSchema = z
    .object({
    part: schedulePartSchema,
    slot_id: uuidSchema,
    start_local_time: localTimeSchema,
    items: z.array(playerSchedulePartResponseItemSchema),
})
    .strict();
// ---------------------------------------------------------------------------
// player-self-service-v0.1.0 (arch §3.2, TASK-362) — keyset preset search for
// the self-service schedule editor. Lets Standard/Pro accounts pick a preset
// per day-part. Tier-aware on the server side; `tier_access` echoed so the FE
// can grey out presets above the venue's tariff. Cursor-based pagination
// (opaque keyset cursor). All `.strict()`.
// ---------------------------------------------------------------------------
/**
 * `GET /player/presets` query string (arch §3.2). `q` is an optional
 * trimmed substring filter (1..120). `limit` is bounded so the picker cannot
 * scan the table. `cursor` is the opaque keyset cursor echoed from the
 * previous page's `next_cursor`.
 */
export const playerPresetSearchQuerySchema = z
    .object({
    q: z.string().trim().min(1).max(120).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z.string().max(1024).optional(),
})
    .strict();
/**
 * Lifetime of every presigned cover GET the player is handed
 * (`PlayerCoverService`, `preset_cover_url`, `cover_url`).
 *
 * Shared rather than private to the API because the player has to reason about
 * it too: a venue tablet can sit on one screen far longer than ten minutes, so
 * the client re-mints its cover URLs on a schedule derived from THIS number.
 * Two independent copies of the number would silently drift the day the server
 * TTL changes, and the failure would look like "covers randomly disappear".
 */
export const PLAYER_COVER_PRESIGN_TTL_SEC = 10 * 60;
/**
 * Single preset hit in the self-service picker (arch §3.2). `is_active_override`
 * flags a preset the account explicitly enabled; `is_tier_default` flags the
 * default preset shipped for the venue's tier. `tier_access` lets the FE grey
 * out presets above the current tariff.
 *
 * `cover_url` is the preset's artwork as a presigned GET
 * ({@link PLAYER_COVER_PRESIGN_TTL_SEC}) for `content.presets.cover_s3_key` —
 * the same handle `preset_cover_url` carries on `/player/next-track` and
 * `/player/schedule`, so the picker, the /now screen and the schedule editor
 * all show one artwork per preset. `null` when the preset has no cover or the
 * presigner failed (fail-safe: artwork never blocks the list).
 */
export const playerPresetItemSchema = z
    .object({
    preset_id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    tier_access: z.enum(['lite', 'standard', 'pro']),
    is_active_override: z.boolean(),
    is_tier_default: z.boolean(),
    cover_url: z.string().nullable(),
})
    .strict();
/**
 * `GET /player/presets` response (arch §3.2). `items` is up to `limit`
 * rows; `next_cursor` is the opaque keyset cursor for the next page, `null`
 * when the last page was returned.
 */
export const playerPresetSearchResponseSchema = z
    .object({
    items: z.array(playerPresetItemSchema),
    next_cursor: z.string().nullable(),
})
    .strict();
/**
 * `GET /player/presets/:preset_id/tracks` query string. Cursor-based keyset
 * pagination mirroring `playerPresetSearchQuerySchema` — `cursor` is the
 * opaque token echoed from the previous page's `next_cursor`.
 */
export const playerPresetTracksQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z.string().max(1024).optional(),
})
    .strict();
/**
 * Single track row in the player's preset composition list («Состав»).
 * `duration_seconds` is nullable (tracks may miss duration metadata) — the FE
 * renders `'—'` via `formatDuration`.
 *
 * `cover_url` is the TRACK's artwork (`content.tracks.cover_s3_key`), presigned
 * exactly like the one Likes and catalog search already carry, so a row in
 * «Состав» looks like the same track does everywhere else. `null` when the
 * track has no cover or the presigner failed.
 */
export const playerPresetTrackItemSchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    duration_seconds: z.number().int().nullable(),
    cover_url: z.string().nullable(),
})
    .strict();
/**
 * `GET /player/presets/:preset_id/tracks` response. `total` is the full
 * match count (for the «N треков» header); `next_cursor` is the opaque keyset
 * cursor for the next page, `null` on the last page.
 */
export const playerPresetTracksResponseSchema = z
    .object({
    items: z.array(playerPresetTrackItemSchema),
    total: z.number().int().nonnegative(),
    next_cursor: z.string().nullable(),
})
    .strict();
// ---------------------------------------------------------------------------
// Venue sound settings — `GET|PATCH /player/sound` (player-crossfade-arch §8.2)
// ---------------------------------------------------------------------------
/**
 * Fleet default when a venue has never configured its crossfade
 * (`customers.venues.crossfade_seconds IS NULL`).
 *
 * Distinct from {@link DEFAULT_CROSSFADE_SECONDS} (3), which is the PRESET
 * contract default and is deliberately left alone: presets keep their admin
 * configuration untouched, the venue value simply wins over it (arch §8.3).
 *
 * The SERVER default is authoritative and comes from
 * `PLAYER_CROSSFADE_DEFAULT_SECONDS`; this constant is the same number for
 * clients that need to render a value before the first successful fetch.
 */
export const VENUE_CROSSFADE_DEFAULT_SECONDS = 5;
/**
 * `GET /player/sound` / `PATCH /player/sound` response.
 *
 * DELIBERATELY A NEW ROUTE rather than a field on `playerMeResponseSchema`
 * (arch §8.2). Both of those schemas are `.strict()`, and this feature needs a
 * field in a RESPONSE (which requires bundle-before-API) and a field in a
 * REQUEST (which requires API-before-bundle) at the same time — no deploy
 * order satisfies both. A brand-new route has neither constraint: old bundles
 * never call it, and these schemas were never parsed by anyone before.
 */
export const playerSoundSettingsResponseSchema = z
    .object({
    /** Raw venue value. `null` = never configured → the venue inherits. */
    crossfade_seconds: z.number().int().min(0).max(10).nullable(),
    /** What actually plays: `(venue ?? server default)`, server-clamped. */
    effective_crossfade_seconds: z.number().int().min(0).max(10),
    /**
     * Kill switch for the OVERLAP MECHANISM, not for the duration
     * (`PLAYER_CROSSFADE_OVERLAP_ENABLED`). `false` → the client must fall
     * back to the sequential fade it ships today. Clamping the duration to 0
     * instead would produce HARD CUTS, i.e. worse than before the feature.
     */
    overlap_enabled: z.boolean(),
    /** Upper bound in force right now, so the UI can clamp its slider. */
    max_crossfade_seconds: z.number().int().min(0).max(10),
})
    .strict();
/**
 * `PATCH /player/sound` request. `null` clears the venue's own value and
 * returns it to inheriting the fleet default — the reason the column is
 * nullable rather than `NOT NULL DEFAULT 5`.
 */
export const playerSoundSettingsUpdateRequestSchema = z
    .object({
    crossfade_seconds: z.number().int().min(0).max(10).nullable(),
})
    .strict();
// ---------------------------------------------------------------------------
// A track's transition point — `GET /player/transition/:track_id/:version_id`
// (track-transitions arch §9, ADR-047)
// ---------------------------------------------------------------------------
/**
 * `GET /player/transition/:track_id/:version_id` response: the length of the
 * audio the server would serve for that version now and, when that audio is
 * trimmed and transition points are switched on, where its ending gets
 * noticeably quieter — on that audio's own timeline.
 *
 * - Auth: the player access token (`Authorization: Bearer`), the same session
 *   check as every player route; no tariff or playback-right gate.
 * - Both path ids are UUIDs (400 otherwise); a pair that does not exist is 404
 *   `TRACK_VERSION_NOT_FOUND`; no or a revoked session is 401.
 * - The binding (ADR-047 §3): a device uses the point only when the length it
 *   loaded matches `audio_sec` within 0.25 s and the point lies inside that
 *   audio; otherwise — and for a failed read — the transition starts N seconds
 *   before the end of the audio, as today.
 *
 * DELIBERATELY A NEW READ (ADR-038, ADR-047): no existing `.strict()` player
 * read gains a field. Joins the ADR-042 pin when the app first reads it.
 */
export const playerTrackTransitionResponseSchema = z
    .object({
    track_id: uuidSchema,
    track_version_id: uuidSchema,
    /** Length, s, of the audio this version is served as now; null — unknown (the version's own encode). */
    audio_sec: z.number().positive().nullable(),
    /** Seconds from the start of that audio where its ending gets noticeably quieter; null — none. */
    transition_point_sec: z.number().nonnegative().nullable(),
})
    .strict();
//# sourceMappingURL=playback.js.map